import { SearchRounded } from "@mui/icons-material";
import {
  Box,
  CardActions,
  CardContent,
  InputAdornment,
  TextField as MuiTextField,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSearchParams } from "react-router-dom";
import DataGridJobApplication from "../components/data_grid_job_application/data_grid_job_application";
import {
  downloadJobApplicationService,
  getJobApplicationList,
  updateJobApplicationStatus,
} from "../../../services/jobs/jobs";
import FilterJobApplication from "../components/filer_job_application/filter_job_application";
import { JobApplicationModel } from "../model/job_application_model";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import UpdateJobsStatusToReject from "../components/update_jobs_status/update_jobs_status_popup";
import Loading from "../../../components/loading/loading";
import { downloadFromBlob } from "../../../utils/utils";
import { RECORDS_EXCEEDS_ALERT_MSG } from "../../../constants";
import { useJobApplicationFilterStore } from "../../../store/use-job-application-filter-store";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import OrgSelector from "../../../components/org_selector/org_selector";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import { TableEmptyState, TableFooterPagination } from "../../users/components/table_toolkit/table_toolkit";
import FilterIcon from "../../../assets/filter_icon.svg";
import SortIcon from "../../../assets/sort.svg";
import XLSIcon from "../../../assets/xls_icon.svg";
import SortJobApplicationPopup, {
  DEFAULT_JOB_APPLICATION_SORT_OPTION,
  JobApplicationSortOption,
  jobApplicationSortOptionLabel,
  jobApplicationSortOptionToParams,
} from "../components/sort_job_application_popup/sort_job_application_popup";
import { black, greyEnabled, purple } from "../../../theme/colors";

const SEARCH_DEBOUNCE_MS = 500;
const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function JobApplication({
  page,
  setPage,
  currentTab,
}: {
  page: number;
  setPage: (val: number) => void;
  currentTab: number;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState<string>("");
  const [pageSize, setPageSize] = useState(8);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [sortOption, setSortOption] = useState<JobApplicationSortOption>(
    DEFAULT_JOB_APPLICATION_SORT_OPTION
  );
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<JobApplicationModel | null>(null);
  const [exportSubmitting, setExportSubmitting] = useState(false);

  const {
    stateId,
    cityId,
    districtId,
    categoryId,
    subCategoryId,
    searchValue,
    selectedStatus,
    startDate,
    endDate,
    ageFrom,
    ageTo,
    gender,
    setSearchValue,
  } = useJobApplicationFilterStore();
  const { selectedOrgId } = useSelectedOrgStore();

  const activeFilterCount = [
    stateId,
    cityId,
    districtId,
    categoryId,
    subCategoryId,
    selectedStatus,
    startDate && endDate,
    ageFrom && ageTo,
    gender,
  ].filter(Boolean).length;

  const { showSnackbar } = useFeedback();

  useEffect(() => {
    setSearchInput(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (searchInput === searchValue) return;
    const timeoutId = setTimeout(() => {
      setPage(1);
      setSearchValue(searchInput);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeoutId);
  }, [searchInput, searchValue, setPage, setSearchValue]);

  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, sortOption, selectedOrgId]);

  useEffect(() => {
    const currentPage = searchParams.get("page");
    const newPage = page.toString();
    if (currentPage !== newPage) {
      setSearchParams({ page: newPage, currentTab: currentTab?.toString() }, { replace: true });
    }
  }, [page, setSearchParams, currentTab, searchParams]);

  const { data: jobApplicationList, isLoading, isError } = useQuery(
    [
      "JobApplicationList",
      page,
      pageSize,
      cityId,
      districtId,
      categoryId,
      subCategoryId,
      searchValue,
      stateId,
      selectedStatus,
      startDate,
      endDate,
      ageFrom,
      ageTo,
      gender,
      sortOption,
      selectedOrgId,
    ],
    async () => {
      const { sortBy, sortOrder } = jobApplicationSortOptionToParams(sortOption);
      const response = await getJobApplicationList({
        page,
        size: pageSize,
        city: cityId,
        district: districtId,
        category: categoryId,
        subCategory: subCategoryId,
        keyword: searchValue,
        state: stateId,
        status: selectedStatus,
        startDate,
        endDate,
        ageFrom,
        ageTo,
        gender,
        sortBy,
        sortOrder,
        orgId: selectedOrgId,
      });
      return response?.data?.data?.data as { total: number; list: JobApplicationModel[] } | undefined;
    }
  );

  function updateServiceApiCall({
    statusId,
    jobId,
    comments,
  }: {
    statusId: number;
    jobId: number;
    comments: string;
  }) {
    updateJobApplicationStatus({ comment: comments, statusId, jobId: +jobId! })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Job status updated successfully");
          queryClient.invalidateQueries({ queryKey: ["JobApplicationList"] });
        }
      })
      .catch((e) => {
        if (e.response?.status === 403) {
          showSnackbar("You do not have permission to perform this operation.");
        } else {
          showSnackbar("Something went wrong");
        }
      });
  }

  function downloadJobApplicationExcel() {
    if ((jobApplicationList?.total || 0) <= 1000) {
      setExportSubmitting(true);
      downloadJobApplicationService({
        page: 1,
        size: 1000,
        city: cityId,
        district: districtId,
        category: categoryId,
        subCategory: subCategoryId,
        keyword: searchValue,
        state: stateId,
        status: selectedStatus,
        format: "excel",
        startDate,
        endDate,
        ageFrom,
        ageTo,
        gender,
        orgId: selectedOrgId,
      })
        .then((response) => {
          downloadFromBlob(response, new Date().valueOf() + ".xlsx");
        })
        .catch(() => {
          showSnackbar("Something went wrong while exporting job applications.");
        })
        .finally(() => setExportSubmitting(false));
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
    }
  }

  const count = jobApplicationList?.total || 0;
  const list = jobApplicationList?.list || [];
  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);
  const summaryText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  return (
    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
          <TextField
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            fullWidth
            placeholder="Search by company name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <OrgSelector lockToOwnOrg />
          <ToolbarButton
            icon={XLSIcon}
            label="Export"
            onClick={downloadJobApplicationExcel}
            loading={exportSubmitting}
            disabled={exportSubmitting}
          />
          <ToolbarButton
            icon={SortIcon}
            label={jobApplicationSortOptionLabel(sortOption)}
            onClick={() => setSortDialogOpen(true)}
          />
          <ToolbarButton
            icon={FilterIcon}
            label="Filters"
            badgeCount={activeFilterCount}
            onClick={() => setFilterDialogOpen(true)}
          />
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <Loading />
        </Box>
      ) : isError ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={8} gap={0.5}>
          <Typography fontWeight={600} color={black}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color={greyEnabled}>
            We couldn't load the job applications list. Please try again.
          </Typography>
        </Box>
      ) : showEmpty ? (
        <TableEmptyState message="No job applications found" hint="Try adjusting your search or filters." />
      ) : (
        <DataGridJobApplication
          data={list}
          page={page}
          pageSize={pageSize}
          setSelectedApplication={setSelectedApplication}
          updateServiceApiCall={updateServiceApiCall}
          setRejectDialogOpen={setRejectDialogOpen}
        />
      )}

      {showList && (
        <CardActions sx={{ px: { xs: 0, sm: 1 }, pb: 0, pt: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              summaryText={summaryText}
              rowsPerPage={pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </CardActions>
      )}

      <FilterJobApplication openModal={filterDialogOpen} setOpenModal={setFilterDialogOpen} />

      <SortJobApplicationPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />

      {rejectDialogOpen && (
        <UpdateJobsStatusToReject
          open={rejectDialogOpen}
          onDialogClose={() => setRejectDialogOpen(false)}
          updateServiceStatus={(comments) => {
            updateServiceApiCall({ statusId: 3, comments, jobId: selectedApplication?.id! });
            setRejectDialogOpen(false);
          }}
          title="Reject Job Applicant"
          subtitle="Reason for rejection:"
        />
      )}
    </CardContent>
  );
}
